import { TextCodes } from '@api/types/translation/TextCodes';
import { useLazyGetProductNoValidQuantityQuery } from '@api/warranty.api';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import { t } from '@translations';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import { useWarrantyClaimContext } from '../WarrantyClaim/WarrantyClaimProvider';

type WarrantySearchPartProps = {
  partRowIndex: number;
};

export const WarrantySearchPart: React.FC<WarrantySearchPartProps> = ({
  partRowIndex,
}: WarrantySearchPartProps) => {
  const { form, isReadOnly } = useWarrantyClaimContext();
  const [trigger] = useLazyGetProductNoValidQuantityQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [partNo, setPartNo] = useState('');
  const debouncedPartNo = useDebounce(partNo, 800);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue(`parts.${partRowIndex}.partNo`, e.target.value, { shouldDirty: true });
    setPartNo(e.target.value);
  };

  useEffect(() => {
    const fetchQuantity = async () => {
      if (debouncedPartNo) {
        setIsLoading(true);
        const res = await trigger(debouncedPartNo).unwrap();
        setIsLoading(false);
        const validPrice = res?.data?.validPrice;
        const validQuantity = res?.data?.validQuantity;
        const productDisplayName = res?.data?.productDisplayName;
        if (res?.data?.productNo) {
          form.setValue(`parts.${partRowIndex}.partNo`, res?.data?.productNo);
        }
        form.setValue(`parts.${partRowIndex}.price`, validPrice);
        form.setValue(`parts.${partRowIndex}.maxQuantity`, validQuantity);
        form.setValue(`parts.${partRowIndex}.totalPrice`, validPrice);
        form.setValue(`parts.${partRowIndex}.designation`, productDisplayName);
        form.setValue(`parts.${partRowIndex}.quantity`, 1);

        if (validQuantity === 0) {
          form.setError(`parts.${partRowIndex}.partNo`, {
            type: 'manual',
            message: t(TextCodes.Warranty.Block.PartNotValid),
          });
        }
        if (validQuantity !== 0) {
          form.clearErrors(`parts.${partRowIndex}.partNo`);
          form.clearErrors(`parts.${partRowIndex}.quantity`);
          form.clearErrors(`parts.${partRowIndex}.price`);
          form.clearErrors(`parts.${partRowIndex}.designation`);
        }
      }
    };

    fetchQuantity();
  }, [debouncedPartNo, partRowIndex, trigger]);

  return (
    <Controller
      name={`parts.${partRowIndex}.partNo`}
      control={form.control}
      rules={{
        required: true,
        validate: {
          validPart: (value) => {
            return value !== '' && form.getValues('parts')?.[partRowIndex]?.maxQuantity !== 0;
          },
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <TextField
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& label': {
              display: { xs: 'block', sm: partRowIndex === 0 ? 'block' : 'none' },
            },
          }}
          label={t(TextCodes.Warranty.Block.PartNumber)}
          onChange={onChange}
          error={!!error}
          defaultValue={form.getValues('parts')?.[partRowIndex]?.partNo || ''}
          InputProps={{
            readOnly: isReadOnly,
            endAdornment: (
              <InputAdornment
                position='end'
                sx={{
                  visibility: isLoading ? 'visible' : 'hidden',
                }}
              >
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
